import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RialSymbol from '../components/RialSymbol';
import customerService, { MenuItemForOrder, RestaurantSummary } from '../services/customer.service';
import { useMenuCart } from '../services/menuCart';
import LoadingAnimation from '../components/LoadingAnimation';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  Remove as RemoveIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ShoppingBagOutlined as PickupIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

export default function RestaurantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cart = useMenuCart();

  const [restaurant, setRestaurant] = useState<RestaurantSummary | null>(null);
  const [menu, setMenu] = useState<MenuItemForOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(false);
    Promise.all([customerService.getRestaurant(id), customerService.getRestaurantMenu(id)])
      .then(([restaurantData, menuData]) => {
        setRestaurant(restaurantData);
        setMenu(menuData);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const grouped = useMemo(() => {
    const map = new Map<string, MenuItemForOrder[]>();
    for (const item of menu) {
      const category = item.category || 'Other';
      if (!map.has(category)) map.set(category, []);
      map.get(category)!.push(item);
    }
    return Array.from(map.entries());
  }, [menu]);

  useEffect(() => {
    if (!activeCategory && grouped.length > 0) setActiveCategory(grouped[0][0]);
  }, [grouped, activeCategory]);

  const jumpToCategory = (category: string) => {
    setActiveCategory(category);
    sectionRefs.current[category]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) return <LoadingAnimation />;
  if (error || !restaurant) {
    return <Alert severity="error">Could not load this restaurant. Please go back and try again.</Alert>;
  }

  const image = restaurant.images?.[0] || restaurant.logo;
  const subtitle = [restaurant.category, restaurant.cuisineTags?.slice(0, 2).join(', ')]
    .filter(Boolean)
    .join(' · ');
  const rating = restaurant.rating?.average ?? 0;
  const reviewCount = restaurant.rating?.count ?? 0;

  // Merchants often type the city into the address as well, which is how you
  // end up with "Muscat, Sultanate of Oman, Muscat".
  const address = (restaurant.location?.address || '').trim();
  const city = (restaurant.location?.city || '').trim();
  const addressLine =
    !city || (address && address.toLowerCase().includes(city.toLowerCase()))
      ? address
      : [address, city].filter(Boolean).join(', ');

  // Adding from this restaurant never disturbs items already in the cart from
  // other restaurants: each keeps its own bucket and its own order.
  const hereCount = id ? cart.itemCountFor(id) : 0;
  const hereTotal = id ? cart.subtotalFor(id) : 0;
  const otherCount = cart.itemCount - hereCount;
  const showCartBar = cart.itemCount > 0;

  return (
    <Box sx={{ pb: showCartBar ? 12 : 2 }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 200, sm: 260 },
          borderRadius: 3.5,
          overflow: 'hidden',
          bgcolor: '#F4EDE1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {image && (
          <>
            {/* Blurred fill behind, whole image in front: photos still fill
                the frame, square logos aren't cropped to pieces. */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(28px)',
                transform: 'scale(1.15)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${image})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </>
        )}
        {!image && <StorefrontIcon sx={{ fontSize: 56, color: 'text.disabled' }} />}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.5) 100%)',
          }}
        />
        <IconButton
          onClick={() => navigate('/restaurants')}
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
          }}
          size="small"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Info card, overlapping the hero */}
      <Box
        sx={{
          position: 'relative',
          mt: -5,
          mx: { xs: 1.5, sm: 3 },
          p: 2.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3.5,
          boxShadow: '0 10px 30px rgba(35,31,22,0.12)',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          {restaurant.name}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.4 }}>
            {subtitle}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 1.5, flexWrap: 'wrap' }}>
          {rating > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                bgcolor: 'primary.main',
                color: '#fff',
                px: 1,
                py: 0.4,
                borderRadius: 999,
              }}
            >
              <StarIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{rating.toFixed(1)}</Typography>
            </Box>
          )}
          {reviewCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {reviewCount === 1 ? '1 review' : `${reviewCount} reviews`}
            </Typography>
          )}
          <Chip
            icon={<PickupIcon sx={{ fontSize: 14 }} />}
            label="Pickup"
            size="small"
            sx={{ bgcolor: '#FDEAE1', color: '#B5290A', fontWeight: 700, border: 'none' }}
          />
        </Box>
        {addressLine && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              mt: 1.8,
              pt: 1.8,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {addressLine}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sticky category bar */}
      {grouped.length > 1 && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            py: 1.5,
            mb: 1,
            bgcolor: 'background.default',
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {grouped.map(([category]) => (
            <Chip
              key={category}
              label={category}
              onClick={() => jumpToCategory(category)}
              color={activeCategory === category ? 'primary' : 'default'}
              variant={activeCategory === category ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700, flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      {grouped.length === 0 ? (
        <Alert severity="info">This restaurant hasn't added any menu items yet.</Alert>
      ) : (
        grouped.map(([category, items]) => (
          <Box
            key={category}
            ref={(el: HTMLDivElement | null) => {
              sectionRefs.current[category] = el;
            }}
            sx={{ mb: 3, scrollMarginTop: '72px' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                {category}
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ fontWeight: 600 }}>
                {items.length}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {items.map((item) => {
                const soldOut =
                  item.status !== 'active' || (item.inventory?.remainingQuantity ?? 0) <= 0;
                const qty = cart.quantityOf(item._id);
                const maxed = qty >= (item.inventory?.remainingQuantity ?? 0);
                return (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                      border: qty > 0 ? '1.5px solid' : '1px solid',
                      borderColor: qty > 0 ? 'primary.main' : 'divider',
                      borderRadius: 3,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15.5 }}>{item.name}</Typography>
                      {soldOut ? (
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.4 }}>
                          Sold out
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            mt: 0.4,
                            fontWeight: 800,
                            fontSize: 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <RialSymbol /> {item.price.toFixed(2)}
                        </Typography>
                      )}
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ width: 118, flexShrink: 0, textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 118,
                          height: 96,
                          borderRadius: 2.5,
                          bgcolor: '#F4EDE1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundImage: item.images?.[0] ? `url(${item.images[0]})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      >
                        {!item.images?.[0] && <RestaurantMenuIcon sx={{ color: 'text.disabled' }} />}
                      </Box>

                      {!soldOut && (
                        <Box sx={{ mt: -2.2, display: 'flex', justifyContent: 'center' }}>
                          {qty > 0 ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: 'primary.main',
                                borderRadius: 999,
                                boxShadow: 2,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => cart.removeItem(item._id)}
                                sx={{ color: '#fff' }}
                              >
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 13.5, minWidth: 16 }}>
                                {qty}
                              </Typography>
                              <IconButton
                                size="small"
                                disabled={maxed}
                                onClick={() => cart.addItem(item)}
                                sx={{ color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)' } }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                              disabled={maxed}
                              onClick={() => cart.addItem(item)}
                              sx={{
                                bgcolor: 'background.paper',
                                borderRadius: 999,
                                fontWeight: 800,
                                letterSpacing: 0.5,
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                boxShadow: 1,
                                px: 2,
                                '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.dark' },
                              }}
                            >
                              ADD
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))
      )}

      {showCartBar && (
        <Box
          sx={{
            position: 'fixed',
            left: { xs: 16, sm: 280 },
            right: 16,
            bottom: 16,
            zIndex: 10,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<ShoppingCartIcon />}
            onClick={() => navigate('/menu-cart')}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              boxShadow: '0 12px 30px rgba(5,64,38,0.35)',
              textAlign: 'left',
            }}
          >
            <Box component="span">
              <Box component="span" sx={{ display: 'block' }}>
                {hereCount > 0
                  ? `${hereCount} item${hereCount === 1 ? '' : 's'} · View Cart`
                  : `${otherCount} item${otherCount === 1 ? '' : 's'} · View Cart`}
              </Box>
              {(hereCount === 0 || otherCount > 0) && (
                <Box
                  component="span"
                  sx={{ display: 'block', fontSize: 11.5, opacity: 0.85, fontWeight: 600 }}
                >
                  {hereCount > 0
                    ? `+${otherCount} from other restaurants`
                    : 'from other restaurants'}
                </Box>
              )}
            </Box>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RialSymbol /> {(hereCount > 0 ? hereTotal : cart.total).toFixed(2)}
            </Box>
          </Button>
        </Box>
      )}
    </Box>
  );
}
